/**
 * O vocabulário do cartão vivo e da gaveta de assinatura (Minutas, 29/09/2026).
 *
 * Funções puras: recebem o retrato do envelope (`content_attributes.data.
 * assinatura`, já em camelCase) e dizem o que desenhar. A REGRA do negócio
 * (provas por preset, estado do envelope) mora no servidor; aqui só a leitura.
 */

export const PASSOS = ['oque', 'quem', 'seguranca', 'revisar'];
export const PRESETS = ['padrao', 'reforcada', 'simples'];
export const PRAZOS = [3, 7, 15, 30, 0];
export const PAPEIS_EXTRAS = ['testemunha', 'avalista', 'representante'];

// As provas que um assinante pode dar (as mesmas chaves de
// `Assinaturas::Presets::NOMES` no servidor).
export const PROVAS = ['selfie', 'documento', 'codigo', 'desenhada'];

const ABERTOS = ['preparando', 'aguardando', 'concluindo'];

export const estaAberto = dados => ABERTOS.includes(dados?.status);

const doPapel = (dados, papel) =>
  (dados?.assinantes || []).find(a => a.papel === papel);
const todosDoPapel = (dados, papel) =>
  (dados?.assinantes || []).filter(a => a.papel === papel);

/**
 * As cinco etapas do cartão, na ordem em que acontecem. `atual` é a primeira
 * que ainda não aconteceu, só enquanto o envelope está aberto.
 */
export function etapasDoCartao(dados) {
  const cliente = doPapel(dados, 'cliente') || {};
  // Pelo escritório assinam os sócios escolhidos (30/09/2026): a etapa acende
  // quando TODOS assinaram, na hora do último; sem advogado no envio
  // (procuração só do cliente), ela não existe.
  const advogados = todosDoPapel(dados, 'advogado');
  const assinaturasDoEscritorio = advogados
    .map(a => a.assinouEm)
    .filter(Boolean)
    .sort();
  const status = dados?.status;
  const saiu =
    Boolean(dados?.linkEnviadoEm) ||
    ['concluindo', 'concluido'].includes(status);
  const etapas = [
    { chave: 'enviado', feita: saiu, quando: dados?.linkEnviadoEm },
    {
      chave: 'abriu',
      feita: ['abriu', 'assinou'].includes(cliente.status),
      quando: cliente.viuEm,
    },
    {
      chave: 'cliente',
      feita: cliente.status === 'assinou',
      quando: cliente.assinouEm,
    },
    advogados.length && {
      chave: 'advogado',
      feita: advogados.every(a => a.status === 'assinou'),
      quando: advogados.every(a => a.status === 'assinou')
        ? assinaturasDoEscritorio.at(-1)
        : null,
    },
    {
      chave: 'concluido',
      feita: status === 'concluido',
      quando: dados?.concluidoEm,
    },
  ].filter(Boolean);
  const indiceAtual = estaAberto(dados) ? etapas.findIndex(e => !e.feita) : -1;
  return etapas.map((e, i) => ({ ...e, atual: i === indiceAtual }));
}

/** Quanto do caminho já foi feito, de 0 a 1 — a barra do cartão. */
export function progresso(dados) {
  const etapas = etapasDoCartao(dados);
  return etapas.filter(e => e.feita).length / etapas.length;
}

/**
 * O tom do selo. Cor só onde carrega significado (design-system): azul anda,
 * âmbar pede você, rubi deu errado, teal concluiu, cinza parou.
 */
export function tomDoStatus(dados) {
  if (
    dados?.falha &&
    !['concluido', 'cancelado', 'recusado'].includes(dados?.status)
  ) {
    return 'alerta';
  }
  if (dados?.expirado) return 'ambar';
  return (
    {
      concluido: 'ok',
      cancelado: 'neutro',
      recusado: 'alerta',
      falhou: 'alerta',
      aguardando: 'azul',
      concluindo: 'azul',
    }[dados?.status] || 'neutro'
  );
}

export const chaveDoStatus = dados =>
  dados?.expirado ? 'expirado' : dados?.status || 'preparando';

/**
 * "Contrato de honorários — Maria" a partir do primeiro documento escolhido.
 * Minuta que já traz o cliente no título fica como está — saía "Procuração —
 * Maria Teste da Silva — Maria" (04/10/2026).
 */
export function nomeSugerido(documentos, nomeDoCliente) {
  const primeiro = documentos[0]?.nome;
  if (!primeiro) return '';
  const nome = (nomeDoCliente || '').trim().split(/\s+/)[0];
  // Vários documentos (as procurações dos filhos e o contrato, 29/10/2026):
  // o primeiro título sozinho não dizia o que ia.
  if (documentos.length > 1) {
    return nome
      ? `${documentos.length} documentos — ${nome}`
      : `${documentos.length} documentos`;
  }
  if (!nome) return primeiro;
  const palavras = primeiro.toLocaleLowerCase('pt-BR').split(/[^\p{L}]+/u);
  if (palavras.includes(nome.toLocaleLowerCase('pt-BR'))) return primeiro;
  return `${primeiro} — ${nome}`;
}

// Datas do cartão e da gaveta. `Intl` direto: o i18n do produto não tem
// formato de data nomeado, e `d(x, 'short')` devolvia vazio ("até ").
const DIA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const DIA_E_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});
export const dataCurta = valor => (valor ? DIA.format(new Date(valor)) : '');
export const diaEHora = valor =>
  valor ? DIA_E_HORA.format(new Date(valor)) : '';

/**
 * PROCURAÇÃO SÓ O CLIENTE ASSINA (Eduardo, 02/10/2026): "procuração precisa só
 * da assinatura do cliente; contrato precisaria dos dois". O tipo vem do
 * documento (`power_of_attorney`); sem tipo (PDF solto), vale o nome.
 */
export const ehProcuracao = doc =>
  doc?.tipo === 'power_of_attorney' ||
  /procura[çc][ãa]o/i.test(doc?.nome || '');

/**
 * Os documentos que o advogado assina quando ninguém escolheu à mão (as
 * `chave`s), no mesmo formato da EscolhaDoAssinante: `null` = todos (nenhuma
 * procuração no envio); `[]` = nenhum (o envio é só procuração); senão, todos
 * menos as procurações.
 */
export function documentosDoAdvogadoPadrao(docs) {
  const lista = docs || [];
  const assina = lista.filter(doc => !ehProcuracao(doc)).map(doc => doc.chave);
  if (assina.length === lista.length) return null;
  return assina;
}
